import { useEffect } from 'react';
import { skipToken } from '@reduxjs/toolkit/query/react';

import { useGetApplicationQuery } from '@/redux/slices/applicationApiSlice';
import {
  isEmployeeNotInDirectoryError,
  useGetEmployeeFromDirectoryQuery,
  useGetOrganizationGroupsForEmployeeQuery,
} from '@/redux/slices/employeeApiSlice';
import { useIsRedactedByDivisions } from '@/hooks/useGetRequestTypeCategoryChampionEmployee';
import {
  useGetRequestByIdQuery,
  useGetRequestTypeByIdQuery,
  useListAllSystemRequestersQuery,
} from '@/redux/slices/requestApiSlice';
import { useAppSelector } from '@/redux/store';
import { Application } from '@/types/applications';
import { Employee } from '@/types/employees';
import { Group } from '@/types/groups';
import { Request } from '@/types/requests';
import { useGetOrganizationApplicationQuery } from '@/redux/slices/organizationApiSlice';

export type ApplicationRequestData = Request & {
  onBehalfOfEmployee?: Employee;
  employeeGroups?: Group[];
  application?: Application;
};

type RequestDataOptions = {
  includeRequestType?: boolean;
  includeApplication?: boolean;
  includeEmployee?: boolean;
  includeEmployeeGroups?: boolean;
  includeSystemRequester?: boolean;
  includeOrgApplication?: boolean;
  // Set by pages that render a Restricted employee: a miss for a user restricted
  // by divisions is then expected, so it isn't logged. Other pages still fail on
  // it and keep logging.
  handlesRedactedEmployee?: boolean;
};

type UseGetNotificationDataProps = {
  requestId: string | null;
  options?: RequestDataOptions;
};

const logError = (message: string) => {
  window?.DD_RUM?.addError(message, {
    location: 'Get request data',
  });
};

export const useGetRequestData = ({
  requestId,
  options = {},
}: UseGetNotificationDataProps) => {
  // Default to fetching only basic request data for optimal performance
  const {
    includeRequestType = false,
    includeApplication = false,
    includeEmployee = false,
    includeEmployeeGroups = false,
    includeSystemRequester = false,
    includeOrgApplication = false,
    handlesRedactedEmployee = false,
  } = options;

  const organizationId = useAppSelector(
    state => state.loggedUser?.organizationId
  );

  // Get request (always needed)
  const {
    data: request,
    isLoading: loadingRequest,
    error: requestError,
  } = useGetRequestByIdQuery(
    !organizationId || !requestId
      ? skipToken
      : {
          organizationId,
          requestId,
        }
  );

  useEffect(() => {
    if (requestError) {
      logError(
        `Error fetching request with requestId: ${requestId} and organizationId: ${organizationId}. Error: ${requestError}`
      );
    }
  }, [organizationId, requestError, requestId]);

  // Get request type (conditional)
  const requestTypeId = request?.requestTypeId ?? null;
  const {
    data: requestType,
    isLoading: loadingRequestType,
    error: requestTypeError,
  } = useGetRequestTypeByIdQuery(
    !includeRequestType || !organizationId || !requestTypeId
      ? skipToken
      : {
          requestTypeId,
        }
  );

  useEffect(() => {
    if (requestTypeError && includeRequestType) {
      logError(
        `Error fetching requestType with requestTypeId: ${requestTypeId}. Error: ${requestTypeError}`
      );
    }
  }, [includeRequestType, organizationId, requestTypeError, requestTypeId]);

  // Get application (conditional)
  const applicationKey = request?.data?.applicationKey ?? null;
  const {
    data: application,
    isLoading: loadingApp,
    error: applicationError,
  } = useGetApplicationQuery(
    !includeApplication || !applicationKey
      ? skipToken
      : {
          applicationKey,
        }
  );

  // Get organization application
  const { data: orgApplication, isLoading: loadingOrgApp } =
    useGetOrganizationApplicationQuery(
      includeOrgApplication && organizationId && applicationKey
        ? {
            organizationId,
            applicationKey,
          }
        : skipToken
    );

  useEffect(() => {
    if (applicationError && includeApplication) {
      logError(
        `Error fetching application with applicationKey: ${applicationKey} and organizationId: ${organizationId}. Error: ${applicationError}`
      );
    }
  }, [applicationError, applicationKey, includeApplication, organizationId]);

  // Get employee (conditional)
  const employeeId = request?.onBehalfOf ?? null;
  const {
    data: employee,
    isLoading: loadingEmployee,
    error: employeeError,
  } = useGetEmployeeFromDirectoryQuery(
    !includeEmployee || !employeeId || !organizationId
      ? skipToken
      : {
          organizationId,
          employeeId,
        }
  );

  const isEmployeeMissingFromDirectory =
    includeEmployee &&
    isEmployeeNotInDirectoryError(!!employeeError, employeeError);
  const isEmployeeRedacted = useIsRedactedByDivisions(
    isEmployeeMissingFromDirectory
  );

  useEffect(() => {
    const isExpectedRedaction = handlesRedactedEmployee && isEmployeeRedacted;
    if (employeeError && includeEmployee && !isExpectedRedaction) {
      logError(
        `Error fetching employee with employeeId: ${employeeId} and organizationId: ${organizationId}. Error: ${employeeError}`
      );
    }
  }, [
    employeeError,
    employeeId,
    handlesRedactedEmployee,
    includeEmployee,
    isEmployeeRedacted,
    organizationId,
  ]);

  // Get employee groups (conditional)
  const {
    data: employeeGroups,
    isLoading: loadingGroups,
    error: groupsError,
  } = useGetOrganizationGroupsForEmployeeQuery(
    !includeEmployeeGroups || !employeeId || !organizationId
      ? skipToken
      : {
          employeeId,
          organizationId,
        }
  );

  useEffect(() => {
    if (groupsError && includeEmployeeGroups) {
      logError(
        `Error fetching employee groups with employeeId: ${employeeId} and organizationId: ${organizationId}. Error: ${groupsError}`
      );
    }
  }, [employeeId, groupsError, includeEmployeeGroups, organizationId]);

  // Get system requester (conditional)
  const systemRequesterId = request?.systemRequesterId;
  const { data: systemRequesters, isFetching: fetchingSystemRequesters } =
    useListAllSystemRequestersQuery(
      includeSystemRequester ? undefined : skipToken
    );

  const systemRequester = systemRequesters?.find(
    sr => sr.id === systemRequesterId
  );

  // Calculate loading state based on what's being fetched
  const isLoading =
    loadingRequest ||
    (includeRequestType && loadingRequestType) ||
    (includeApplication && loadingApp) ||
    (includeEmployee && loadingEmployee) ||
    (includeEmployeeGroups && loadingGroups) ||
    (includeSystemRequester && fetchingSystemRequesters) ||
    (includeOrgApplication && loadingOrgApp);

  return {
    data: {
      request,
      requestType: includeRequestType ? requestType : undefined,
      application: includeApplication ? application : undefined,
      employee: includeEmployee ? employee : undefined,
      employeeGroups: includeEmployeeGroups ? employeeGroups : undefined,
      systemRequester: includeSystemRequester ? systemRequester : undefined,
      orgApplication: includeOrgApplication ? orgApplication : undefined,
    },
    isLoading,
    isEmployeeRedacted,
    requestError,
  };
};
